import React, { useEffect, useRef, useState } from 'react';
import './BlogPage.scss';
import { Helmet } from 'react-helmet-async';
import { baseStrapiURL, baseURL } from '../../services/config';
import { useContentQuery } from '../../services/strapi/hooks';
import Loading from '../../components/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import Error404Page from '../Error404Page/Error404Page';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import BlogCard from './components/BlogCard/BlogCard';
import BlogFilters from './components/BlogFilters/BlogFilters';
import BlogPagination from './components/BlogPagination/BlogPagination';
import { useBlogQuery } from '../../hooks/useBlogQuery';
import { useBlogNavigation } from '../../hooks/useBlogNavigation';
import TitlePage from '../../components/TitlePage/TitlePage';

const BlogPage = () => {
    const strapiPage = 'blog';
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isComingFromArticle } = useBlogNavigation();

    // État pour les filtres et pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('DateEnvoi:desc');
    const [selectedCategory, setSelectedCategory] = useState('toutes');

    // Ref pour distinguer les changements de pagination des changements de filtres
    const isFilterChange = useRef(false);
    const hasRestoredState = useRef(false);

    // Récupération de la config de la page blog
    const { data: strapiData, isLoading, error } = useContentQuery(strapiPage);

    // Obtenir les données mises en cache ou les données actuelles
    const displayData = strapiData || queryClient.getQueryData(['strapi-content', strapiPage]);

    // Configuration par défaut si pas encore chargée
    const pageSize = displayData?.Configuration?.NewslettersParPage || 12;
    const showFilters = displayData?.Configuration?.AfficherFiltres ?? true;
    const showPagination = displayData?.Configuration?.AfficherPagination ?? true;

    // Récupération des articles/newsletters avec le hook personnalisé
    const {
        data: blogData,
        isLoading: blogLoading,
        error: blogError
    } = useBlogQuery({
        page: currentPage,
        pageSize,
        sort: sortOrder,
        search: searchTerm,
        categorie: selectedCategory
    });

    // N'afficher le chargement que si les données ne sont pas disponibles
    const showLoading = isLoading && !displayData;

    // Fonction pour mettre à jour l'URL avec les paramètres actuels
    const updateURL = (newPage, newSearch, newSort, newCategory) => {
        const params = new URLSearchParams();
        
        if (newPage > 1) params.set('page', newPage.toString());
        if (newSearch) params.set('search', newSearch);
        if (newSort !== 'DateEnvoi:desc') params.set('sort', newSort);
        if (newCategory !== 'toutes') params.set('category', newCategory);
        
        const queryString = params.toString();
        const newUrl = queryString ? `/blog?${queryString}` : '/blog';
        
        // Mettre à jour l'URL sans recharger la page
        navigate(newUrl, { replace: true });
    };

    // Restauration de l'état au montage du composant
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const pageFromUrl = urlParams.get('page');
        const searchFromUrl = urlParams.get('search');
        const sortFromUrl = urlParams.get('sort');
        const categoryFromUrl = urlParams.get('category');

        // Si on vient d'un article avec un état à restaurer
        if (isComingFromArticle() && location.state?.restoreState && !hasRestoredState.current) {
            const savedState = location.state.restoreState;
            
            setCurrentPage(savedState.currentPage || 1);
            setSearchTerm(savedState.searchTerm || '');
            setSortOrder(savedState.sortOrder || 'DateEnvoi:desc');
            setSelectedCategory(savedState.selectedCategory || 'toutes');
            
            hasRestoredState.current = true;

            // Restaurer la position de scroll après le rendu
            if (savedState.scrollPosition && location.state.shouldRestoreScroll) {
                setTimeout(() => {
                    window.scrollTo(0, savedState.scrollPosition);
                }, 100);
            }
        }
        // Sinon, vérifier les paramètres d'URL
        else if (pageFromUrl || searchFromUrl || sortFromUrl || categoryFromUrl) {
            if (pageFromUrl) setCurrentPage(parseInt(pageFromUrl) || 1);
            if (searchFromUrl) setSearchTerm(searchFromUrl);
            if (sortFromUrl) setSortOrder(sortFromUrl);
            if (categoryFromUrl) setSelectedCategory(categoryFromUrl);
        }
    }, [location, isComingFromArticle]);

    // Gestion du scroll lors des changements de page
    useEffect(() => {
        if (!isFilterChange.current && !isComingFromArticle()) {
            window.scroll(0, 0);
        }
        isFilterChange.current = false;
    }, [currentPage, isComingFromArticle]);

    // Fonction pour gérer la recherche
    const handleSearch = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
        isFilterChange.current = true;
        updateURL(1, term, sortOrder, selectedCategory);
    };

    // Fonction pour gérer le changement de tri
    const handleSortChange = (newSort) => {
        setSortOrder(newSort);
        setCurrentPage(1);
        isFilterChange.current = true;
        updateURL(1, searchTerm, newSort, selectedCategory);
    };

    // Fonction pour gérer le changement de catégorie
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        setCurrentPage(1);
        isFilterChange.current = true;
        updateURL(1, searchTerm, sortOrder, category);
    };

    // Fonction pour gérer le changement de page
    const handlePageChange = (page) => {
        setCurrentPage(page);
        updateURL(page, searchTerm, sortOrder, selectedCategory);
    };

    if (showLoading) return <Loading />;
    if (!displayData || displayData.length === 0 || error) return <Error404Page />;

    const blogPosts = blogData?.data || [];
    const pagination = blogData?.meta?.pagination || {};

    // État actuel pour la sauvegarde
    const currentBlogState = {
        currentPage,
        searchTerm,
        sortOrder,
        selectedCategory
    };

    return (
        <div className="blog-page">
            <Helmet>
                {/* Open Graph (utilisé par Facebook, LinkedIn, etc.) */}
                <meta property="og:title" content={displayData.SEO.TitreOnglet} />
                <meta property="og:description" content={displayData.SEO.Description} />
                <meta property="og:image" content={baseStrapiURL + displayData.SEO.PhotoReseauxSociaux_1200x630.url} />
                <meta property="og:url" content={displayData.Slug === null ? baseURL : baseURL + '/' + displayData.Slug} />
                <meta property="og:type" content="website" />

                {/* Twitter Cards */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={displayData.SEO.TitreOnglet} />
                <meta name="twitter:description" content={displayData.SEO.Description} />
                <meta name="twitter:image" content={baseStrapiURL + displayData.SEO.PhotoReseauxSociaux_1200x630.url} />
                <meta name="twitter:url" content={displayData.Slug === null ? baseURL : baseURL + '/' + displayData.Slug} />

                <title>{displayData.SEO.TitreOnglet}</title>
                <meta name="description" content={displayData.SEO.Description} />
            </Helmet>

            <TitlePage
                picture={displayData.TitrePage_section.Photo}
                title1={displayData.TitrePage_section.Titre1}
                font1={displayData.TitrePage_section.Typographie1 === 'Great Vibes' ? "handwrite-title" : "classic-title"}
                title2={displayData.TitrePage_section.Titre2}
                font2={displayData.TitrePage_section.Typographie2 === 'Great Vibes' ? "handwrite-title" : "classic-title"}
            />

            {/* Contenu principal du blog */}
            <div className="content">
                {/* Filtres */}
                {showFilters && (
                    <BlogFilters
                        searchTerm={searchTerm}
                        sortOrder={sortOrder}
                        selectedCategory={selectedCategory}
                        onSearchChange={handleSearch}
                        onSortChange={handleSortChange}
                        onCategoryChange={handleCategoryChange}
                        resultsCount={pagination.total || 0}
                    />
                )}

                {/* Grille des articles de blog */}
                <div className="blog-page__posts">
                    {blogError ? (
                        <div className="blog-page__error">
                            <p>Erreur lors du chargement des articles.</p>
                        </div>
                    ) : blogPosts.length === 0 ? (
                        <div className="blog-page__empty">
                            <p>Aucun article trouvé{searchTerm && ` pour "${searchTerm}"`}.</p>
                        </div>
                    ) : (
                        <div className="blog-page__grid">
                            {blogPosts.map((post) => (
                                <BlogCard
                                    key={post.id}
                                    post={post}
                                    currentBlogState={currentBlogState}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {showPagination && pagination.pageCount > 1 && (
                    <BlogPagination
                        currentPage={currentPage}
                        totalPages={pagination.pageCount}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default BlogPage;